import { useState } from 'react';
import { noop } from 'lodash';
import { logger } from '@fiverr-private/obs';
import { CollectionFormFields, RawCollection, ResourceType } from '../types';
import { raiseCreateListError, raiseListCreated, raiseSavedToList } from '../components/toasts/toasts';
import { createCollection as apiCreateCollection } from '../services/createCollection';
import { CollectionsContext } from '../context/CollectionsContext';
import { optimisticallyCreateCollection } from '../store/optimisticallyCreateCollection';
import { sendAddItemToListEvent, sendListHasBeenCreatedEvent } from '../utils/events/freelancersListEvents';
import { isCollectionKimeraError } from '../utils/isCollectionKimeraError';
import { useOptionalQueryClient } from './useOptionalQueryClient';
import { INVALIDATE_DELAY } from './constants';
import { useCollections } from './useCollections';

export const useCreateCollection = ({
    resourceType,
    resourceId,
    bi,
    onSuccess = noop,
    onError = noop,
}: {
    resourceType?: ResourceType;
    resourceId?: string;
    bi: CollectionsContext['bi'];
    onSuccess?: (collection: RawCollection) => void;
    onError?: (error?: Error) => void;
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const queryClient = useOptionalQueryClient();
    const { collections } = useCollections();

    const createCollection = async (collection: CollectionFormFields) => {
        setIsLoading(true);
        let rollback = noop;
        const isCollectionExists = collections?.some(
            (c) => c.name === collection.name && c.ownerType === collection.ownerType
        );

        try {
            if (queryClient) {
                rollback = optimisticallyCreateCollection(queryClient, { collection, resourceType, resourceId });
            }

            const initialResource = resourceType && resourceId ? { resourceId, resourceType } : undefined;
            const newCollection = await apiCreateCollection(collection, initialResource);
            setIsLoading(false);
            if (!isCollectionExists) raiseListCreated();
            onSuccess(newCollection);

            if (initialResource) {
                raiseSavedToList(newCollection.slug, collection.name);
            }

            sendCreateEvents(newCollection.id, collection);

            setTimeout(() => {
                queryClient?.invalidateQueries({ queryKey: ['collections'] });
            }, INVALIDATE_DELAY);
        } catch (error) {
            rollback();
            setIsLoading(false);
            if (isCollectionKimeraError(error)) {
                raiseCreateListError(error.response?.data.errorKey);
            } else {
                raiseCreateListError('');
                logger.error(error as Error, { description: 'Error while creating collection' });
            }
            onError(error as Error);
        }
    };

    const sendCreateEvents = (id: string, collection: CollectionFormFields) => {
        const biProps = {
            biContext: bi,
            gigId: resourceType === 'GIG' ? resourceId : undefined,
            sellerId: resourceType === 'SELLER' ? resourceId : undefined,
            collection: {
                id,
                type: collection.ownerType,
                name: collection.name,
                item: {
                    id: resourceId as string,
                    type: resourceType as ResourceType,
                },
            },
        } satisfies Parameters<typeof sendListHasBeenCreatedEvent>[0];

        sendListHasBeenCreatedEvent(biProps);

        if (resourceType && resourceId) {
            sendAddItemToListEvent(biProps);
        }
    };

    return { isLoading, createCollection };
};
